// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/PlanView.yaml
// Generated: 2026-06-16T13:50:13.850Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { planViewService } from '@/services/finance/PlanViewService';
import { CreatePlanView, UpdatePlanView, PlanView } from '@/types/finance/PlanView';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for PlanView
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const planviewKeys = {
  all: (tenantId?: string | null) => ['planviews', tenantId] as const,
  lists: (tenantId?: string | null) => [...planviewKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...planviewKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...planviewKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...planviewKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all PlanViews
 */
export function usePlanViews(options?: Omit<UseQueryOptions<PlanView[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<PlanView[], Error>({
    queryKey: planviewKeys.lists(tenantId),
    queryFn: () => planViewService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single PlanView by ID
 */
export function usePlanView(
  id: string,
  options?: Omit<UseQueryOptions<PlanView | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<PlanView | undefined, Error>({
    queryKey: planviewKeys.detail(id, tenantId),
    queryFn: () => planViewService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new PlanView
 */
export function useCreatePlanView() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<PlanView, Error, CreatePlanView, { previousPlanViews?: PlanView[] }>({
    mutationFn: (data: CreatePlanView) => planViewService.create(data),
    onMutate: async (newPlanView) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planviewKeys.lists(tenantId) });

      // Snapshot previous value
      const previousPlanViews = queryClient.getQueryData<PlanView[]>(planviewKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<PlanView[]>(planviewKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newPlanView, id: (newPlanView as { id?: string }).id ?? 'temp-' + Date.now() } as PlanView
      ]);

      return { previousPlanViews };
    },
    onError: (_err, _newPlanView, context) => {
      // Rollback on error
      if (context?.previousPlanViews) {
        queryClient.setQueryData(planviewKeys.lists(tenantId), context.previousPlanViews);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planviewKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing PlanView
 */
export function useUpdatePlanView() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<PlanView, Error, { id: string; data: UpdatePlanView }, { previousPlanView?: PlanView; previousPlanViews?: PlanView[] }>({
    mutationFn: ({ id, data }) => planViewService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planviewKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: planviewKeys.lists(tenantId) });

      // Snapshot previous values
      const previousPlanView = queryClient.getQueryData<PlanView>(planviewKeys.detail(id, tenantId));
      const previousPlanViews = queryClient.getQueryData<PlanView[]>(planviewKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<PlanView>(planviewKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as PlanView);

      // Optimistically update list
      queryClient.setQueryData<PlanView[]>(planviewKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as PlanView : item))
      );

      return { previousPlanView, previousPlanViews };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousPlanView) {
        queryClient.setQueryData(planviewKeys.detail(id, tenantId), context.previousPlanView);
      }
      if (context?.previousPlanViews) {
        queryClient.setQueryData(planviewKeys.lists(tenantId), context.previousPlanViews);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planviewKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: planviewKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a PlanView
 */
export function useDeletePlanView() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousPlanViews?: PlanView[] }>({
    mutationFn: (id: string) => planViewService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planviewKeys.lists(tenantId) });

      // Snapshot previous value
      const previousPlanViews = queryClient.getQueryData<PlanView[]>(planviewKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<PlanView[]>(planviewKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousPlanViews };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousPlanViews) {
        queryClient.setQueryData(planviewKeys.lists(tenantId), context.previousPlanViews);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planviewKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSavePlanView() {
  const createMutation = useCreatePlanView();
  const updateMutation = useUpdatePlanView();

  return {
    mutate: (params: { id?: string; data: CreatePlanView | UpdatePlanView }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdatePlanView }, options);
      } else {
        return createMutation.mutate(params.data as CreatePlanView, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
